package co.maytes.standupbot.dashboard.styles

import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.shared.design.StatusHue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import org.jetbrains.compose.web.css.StyleScope

internal object ControlClass {
    const val BUTTON = "sb-btn"
    const val BUTTON_PRIMARY = "sb-btn-primary"
    const val BUTTON_SECONDARY = "sb-btn-secondary"
    const val BUTTON_GHOST = "sb-btn-ghost"
    const val BUTTON_DANGER = "sb-btn-danger"
    const val BUTTON_DANGER_OUTLINE = "sb-btn-danger-outline"
    const val BUTTON_LINK = "sb-btn-link"
    const val ICON_BUTTON = "sb-icon-btn"
    const val FIELD = "sb-field-control"
    const val FIELD_ADORNMENT = "sb-field-adornment"
    const val FIELD_CLEAR = "sb-field-clear"
    const val FIELD_MESSAGE = "sb-field-message"
    const val FIELD_ERROR = "sb-field-error"
    const val CHECK = "sb-check"
    const val CHECK_INPUT = "sb-check-input"
    const val CHECK_BOX = "sb-check-box"
    const val RADIO_BOX = "sb-radio-box"
    const val RADIO_CARD = "sb-radio-card"
    const val SWITCH = "sb-switch"
    const val SWITCH_THUMB = "sb-switch-thumb"
    const val DAY_PILL = "sb-day-pill"
    const val OPTION = "sb-option"
    const val SELECT_SEARCH = "sb-select-search"
    const val COPY_FIELD = "sb-copy-field"
    const val COPY_FIELD_VALUE = "sb-copy-field-value"
    const val SWITCH_WIDTH_VAR = "--sb-sw-w"
    const val SWITCH_HEIGHT_VAR = "--sb-sw-h"
    const val SWITCH_THUMB_VAR = "--sb-sw-thumb"
    const val SWITCH_TRAVEL_VAR = "--sb-sw-travel"
    const val SWITCH_SQUEEZE_VAR = "--sb-sw-squeeze"
}

private const val ENABLED = ":not([aria-disabled=\"true\"])"
private const val INVALID = "[aria-invalid=\"true\"]"
private const val ERROR_KEYFRAMES = "sb-field-error-in"

private fun InitSilkContext.rule(selector: String, build: StyleScope.() -> Unit) {
    stylesheet.registerStyle(selector) { base { Modifier.styleModifier(build) } }
}

private fun StyleScope.focusRing() {
    property("outline", FocusRing.OUTLINE)
    property("outline-offset", FocusRing.OFFSET)
}

private fun feedbackTransition(): String =
    Motion.transition(
        Motion.DUR_FAST,
        "background-color",
        "color",
        "border-color",
        "box-shadow",
        "opacity",
    )

@InitSilk
fun initControlKeyframes(ctx: InitSilkContext) {
    ctx.stylesheet.registerKeyframes(ERROR_KEYFRAMES) {
        from {
            Modifier.styleModifier {
                property("opacity", "0")
                property("transform", "translateY(${Motion.FIELD_ERROR_RISE_PX}px)")
            }
        }
        to {
            Modifier.styleModifier {
                property("opacity", "1")
                property("transform", "translateY(0)")
            }
        }
    }
}

@InitSilk
fun initControlStyles(ctx: InitSilkContext) {
    ctx.registerButtons()
    ctx.registerFields()
    ctx.registerChecks()
    ctx.registerSwitch()
    ctx.registerPills()
    ctx.registerListbox()
    ctx.fadeInsteadOfMove(".${ControlClass.FIELD_ERROR}", "sb-enter-fade")
}

private fun InitSilkContext.registerButtons() {
    val danger = Palette.status(StatusHue.Danger)
    rule(".${ControlClass.BUTTON}") {
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("box-sizing", "border-box")
        property("flex-shrink", "0")
        property("margin", "0")
        property("font-family", Typography.FONT_SANS)
        property("font-weight", "${FontWeight.medium}")
        property("line-height", "1")
        property("white-space", "nowrap")
        property("text-decoration", "none")
        property("cursor", "pointer")
        property("user-select", "none")
        property("-webkit-appearance", "none")
        property("appearance", "none")
        property("border", "1px solid transparent")
        property("border-radius", "${Radius.control}")
        property("outline", "none")
        property("transition", feedbackTransition())
        property(Motion.PRESS_SCALE_VAR, Motion.BUTTON_PRESS_SCALE)
    }
    rule(".${ControlClass.BUTTON}:focus-visible") { focusRing() }
    rule(".${ControlClass.BUTTON}[aria-disabled=\"true\"]") {
        property("opacity", "0.5")
        property("cursor", "not-allowed")
    }
    rule(".${ControlClass.BUTTON}[aria-busy=\"true\"]") { property("cursor", "progress") }

    rule(".${ControlClass.BUTTON_PRIMARY}") {
        property("background-color", Palette.primary.cssRgb())
        property("color", Palette.onPrimary.cssRgb())
        property("box-shadow", Shadow.XS)
    }
    rule(".${ControlClass.BUTTON_PRIMARY}:hover$ENABLED") {
        property("background-color", Palette.primaryHover.cssRgb())
    }

    rule(".${ControlClass.BUTTON_SECONDARY}") {
        property("background-color", Palette.surface.cssRgb())
        property("color", Palette.textPrimary.cssRgb())
        property("border-color", Palette.border.cssRgb())
        property("box-shadow", Shadow.XS)
    }
    rule(".${ControlClass.BUTTON_SECONDARY}:hover$ENABLED") {
        property("background-color", Palette.surfaceHover.cssRgb())
        property("border-color", Palette.textTertiary.cssRgb())
    }

    rule(".${ControlClass.BUTTON_GHOST}") {
        property("background-color", "transparent")
        property("color", Palette.textPrimary.cssRgb())
    }
    rule(".${ControlClass.BUTTON_GHOST}:hover$ENABLED") {
        property("background-color", Palette.surfaceSubtle.cssRgb())
    }

    rule(".${ControlClass.BUTTON_DANGER}") {
        property("background-color", danger.solid.cssRgb())
        property("color", danger.onSolid.cssRgb())
        property("box-shadow", Shadow.XS)
    }
    rule(".${ControlClass.BUTTON_DANGER}:hover$ENABLED") {
        property("background-color", danger.solidHover.cssRgb())
    }

    rule(".${ControlClass.BUTTON_DANGER_OUTLINE}") {
        property("background-color", "transparent")
        property("color", danger.solid.cssRgb())
        property("border-color", Palette.border.cssRgb())
    }
    rule(".${ControlClass.BUTTON_DANGER_OUTLINE}:hover$ENABLED") {
        property("background-color", danger.subtle.cssRgb())
        property("border-color", danger.border.cssRgb())
    }

    rule(".${ControlClass.BUTTON_LINK}") {
        property("background-color", "transparent")
        property("color", Palette.accentText.cssRgb())
        property("text-decoration", "underline")
        property("text-underline-offset", "${Space.s0_5}")
        property("font-weight", "${FontWeight.medium}")
    }
    rule(".${ControlClass.BUTTON_LINK}:hover$ENABLED") {
        property("color", Palette.accentHover.cssRgb())
    }

    rule(".${ControlClass.ICON_BUTTON}") {
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("box-sizing", "border-box")
        property("flex-shrink", "0")
        property("margin", "0")
        property("padding", "0")
        property("font", "inherit")
        property("cursor", "pointer")
        property("-webkit-appearance", "none")
        property("appearance", "none")
        property("background-color", "transparent")
        property("color", Palette.textSecondary.cssRgb())
        property("border", "1px solid transparent")
        property("border-radius", "${Radius.control}")
        property("outline", "none")
        property("transition", feedbackTransition())
        property(Motion.PRESS_SCALE_VAR, Motion.ICON_BUTTON_PRESS_SCALE)
    }
    rule(".${ControlClass.ICON_BUTTON}:hover$ENABLED") {
        property("background-color", Palette.surfaceSubtle.cssRgb())
        property("color", Palette.textPrimary.cssRgb())
    }
    rule(".${ControlClass.ICON_BUTTON}:focus-visible") { focusRing() }
    rule(".${ControlClass.ICON_BUTTON}[aria-disabled=\"true\"]") {
        property("opacity", "0.5")
        property("cursor", "not-allowed")
    }
    rule(".${ControlClass.ICON_BUTTON}[aria-busy=\"true\"]") { property("cursor", "progress") }
}

private fun InitSilkContext.registerFields() {
    val danger = Palette.status(StatusHue.Danger)
    rule(".${ControlClass.FIELD}") {
        property("box-sizing", "border-box")
        property("margin", "0")
        property("background-color", Palette.surface.cssRgb())
        property("color", Palette.textPrimary.cssRgb())
        property("border", "1px solid ${Palette.border.cssRgb()}")
        property("border-radius", "${Radius.control}")
        property("font-family", Typography.FONT_SANS)
        property("font-weight", "${FontWeight.regular}")
        property("outline", "none")
        property("-webkit-appearance", "none")
        property("appearance", "none")
        property(
            "transition",
            Motion.transition(Motion.DUR_FAST, "border-color", "background-color", "color", "box-shadow"),
        )
    }
    rule(".${ControlClass.FIELD}:hover:not(:focus):not(:disabled)$ENABLED:not($INVALID)") {
        property("border-color", Palette.textTertiary.cssRgb())
    }
    rule(".${ControlClass.FIELD}:focus") {
        focusRing()
        property("border-color", Palette.accent.cssRgb())
    }
    rule(".${ControlClass.FIELD}::placeholder") {
        property("color", Palette.textTertiary.cssRgb())
        property("opacity", "1")
    }
    rule(".${ControlClass.FIELD}:disabled, .${ControlClass.FIELD}[aria-disabled=\"true\"]") {
        property("background-color", Palette.surfaceSubtle.cssRgb())
        property("color", Palette.textTertiary.cssRgb())
        property("cursor", "not-allowed")
    }
    rule(".${ControlClass.FIELD}$INVALID, .${ControlClass.FIELD}$INVALID:focus") {
        property("border-color", danger.solid.cssRgb())
    }

    rule(".${ControlClass.FIELD_ADORNMENT}") {
        property("position", "absolute")
        property("top", "0")
        property("bottom", "0")
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Space.s1}")
        property("pointer-events", "none")
        property("color", Palette.textTertiary.cssRgb())
    }

    rule(".${ControlClass.FIELD_CLEAR}") {
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("padding", "0")
        property("margin", "0")
        property("cursor", "pointer")
        property("-webkit-appearance", "none")
        property("appearance", "none")
        property("background-color", "transparent")
        property("color", Palette.textTertiary.cssRgb())
        property("border", "none")
        property("border-radius", "${Radius.inner}")
        property("opacity", "0")
        property("pointer-events", "none")
        property("transition", Motion.transition(Motion.DUR_FAST, "opacity", "color", "background-color"))
    }
    rule(".${ControlClass.FIELD_CLEAR}.${Motion.OPEN_CLASS}") {
        property("opacity", "1")
        property("pointer-events", "auto")
    }
    rule(".${ControlClass.FIELD_CLEAR}:hover") {
        property("color", Palette.textPrimary.cssRgb())
        property("background-color", Palette.surfaceSubtle.cssRgb())
    }

    rule(".${ControlClass.FIELD_ERROR}") {
        property("animation", "$ERROR_KEYFRAMES ${Motion.DUR_FAST} ${Motion.EASE_STANDARD} both")
    }
}

private fun InitSilkContext.registerChecks() {
    val inputSelector = ".${ControlClass.CHECK} .${ControlClass.CHECK_INPUT}"
    val boxSelector = "$inputSelector + .${ControlClass.CHECK_BOX}, $inputSelector + .${ControlClass.RADIO_BOX}"
    fun box(state: String) = "$inputSelector$state + .${ControlClass.CHECK_BOX}, " +
        "$inputSelector$state + .${ControlClass.RADIO_BOX}"

    rule(".${ControlClass.CHECK}") {
        property("position", "relative")
        property("display", "inline-flex")
        property("align-items", "center")
        property("user-select", "none")
    }
    rule(inputSelector) {
        property("position", "absolute")
        property("top", "0")
        property("left", "0")
        property("width", "100%")
        property("height", "100%")
        property("margin", "0")
        property("opacity", "0")
        property("cursor", "inherit")
    }
    rule(boxSelector) {
        property("position", "relative")
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("flex-shrink", "0")
        property("box-sizing", "border-box")
        property("background-color", Palette.surface.cssRgb())
        property("color", Palette.onAccent.cssRgb())
        property("border", "1px solid ${Palette.textTertiary.cssRgb()}")
        property(
            "transition",
            Motion.transition(Motion.DUR_FAST, "background-color", "border-color", "box-shadow", "opacity"),
        )
    }
    rule(".${ControlClass.CHECK_BOX}") { property("border-radius", "${Radius.inner}") }
    rule(".${ControlClass.RADIO_BOX}") { property("border-radius", "${Radius.pill}") }
    rule(box(":checked")) {
        property("background-color", Palette.accent.cssRgb())
        property("border-color", Palette.accent.cssRgb())
    }
    rule(".${ControlClass.CHECK}:hover $inputSelector:not(:checked):not(:disabled) + .${ControlClass.CHECK_BOX}, " +
        ".${ControlClass.CHECK}:hover $inputSelector:not(:checked):not(:disabled) + .${ControlClass.RADIO_BOX}") {
        property("border-color", Palette.textPrimary.cssRgb())
    }
    rule(box(":focus-visible")) { focusRing() }
    rule(box("[aria-invalid=\"true\"]:not(:checked)")) {
        property("border-color", Palette.status(StatusHue.Danger).solid.cssRgb())
    }

    rule(".${ControlClass.CHECK_BOX} svg") {
        property("width", "100%")
        property("height", "100%")
        property("overflow", "visible")
    }
    rule(".${ControlClass.CHECK_BOX} svg path") {
        property("stroke-dasharray", "${Motion.CHECK_DASH_LENGTH}")
        property("stroke-dashoffset", "${Motion.CHECK_DASH_LENGTH}")
        property("opacity", "0")
        property(
            "transition",
            "stroke-dashoffset ${Motion.DUR_FIELD_CHECK_DRAW} ${Motion.EASE_EMPHASIS}, " +
                "opacity ${Motion.DUR_FAST} ${Motion.EASE_STANDARD}",
        )
    }
    rule("$inputSelector:checked + .${ControlClass.CHECK_BOX} svg path") {
        property("stroke-dashoffset", "0")
        property("opacity", "1")
    }

    rule(".${ControlClass.RADIO_BOX}::after") {
        property("content", "''")
        property("width", "40%")
        property("height", "40%")
        property("border-radius", "${Radius.pill}")
        property("background-color", Palette.onAccent.cssRgb())
        property("opacity", "0")
        property("transform", "scale(${Motion.ICON_SWAP_SCALE})")
        property(
            "transition",
            Motion.transition(Motion.DUR_FAST, "opacity", "transform"),
        )
    }
    rule("$inputSelector:checked + .${ControlClass.RADIO_BOX}::after") {
        property("opacity", "1")
        property("transform", "scale(1)")
    }

    rule(".${ControlClass.RADIO_CARD}") {
        property("position", "relative")
        property("display", "flex")
        property("align-items", "center")
        property("box-sizing", "border-box")
        property("width", "100%")
        property("background-color", Palette.surface.cssRgb())
        property("border", "1px solid ${Palette.border.cssRgb()}")
        property("border-radius", "${Radius.control}")
        property("transition", feedbackTransition())
    }
    rule(".${ControlClass.RADIO_CARD}:hover:not([data-disabled=\"true\"]):not([data-checked=\"true\"])") {
        property("border-color", Palette.textTertiary.cssRgb())
    }
    rule(".${ControlClass.RADIO_CARD}[data-checked=\"true\"]") {
        property("background-color", Palette.accentSubtle.cssRgb())
        property("border-color", Palette.accent.cssRgb())
    }
}

private fun InitSilkContext.registerSwitch() {
    val danger = Palette.status(StatusHue.Danger)
    rule(".${ControlClass.SWITCH}") {
        property("position", "relative")
        property("display", "inline-flex")
        property("align-items", "center")
        property("flex-shrink", "0")
        property("box-sizing", "border-box")
        property("width", "var(${ControlClass.SWITCH_WIDTH_VAR})")
        property("height", "var(${ControlClass.SWITCH_HEIGHT_VAR})")
        property("margin", "0")
        property("padding", "0")
        property("cursor", "pointer")
        property("-webkit-appearance", "none")
        property("appearance", "none")
        property("background-color", Palette.textTertiary.cssRgb())
        property("border", "none")
        property("border-radius", "${Radius.pill}")
        property("outline", "none")
        property(
            "transition",
            Motion.transitionWith(
                Motion.DUR_SWITCH_THUMB,
                Motion.EASE_STANDARD,
                "background-color",
                "box-shadow",
                "opacity",
            ),
        )
    }
    rule(".${ControlClass.SWITCH}:hover$ENABLED") {
        property("background-color", Palette.textSecondary.cssRgb())
    }
    rule(".${ControlClass.SWITCH}[aria-checked=\"true\"], .${ControlClass.SWITCH}[aria-checked=\"true\"]:hover$ENABLED") {
        property("background-color", Palette.accent.cssRgb())
    }
    rule(".${ControlClass.SWITCH}:focus-visible") { focusRing() }
    rule(".${ControlClass.SWITCH}[aria-disabled=\"true\"]") {
        property("opacity", "0.5")
        property("cursor", "not-allowed")
    }
    rule(".${ControlClass.SWITCH}[aria-busy=\"true\"]") { property("cursor", "progress") }
    rule(".${ControlClass.SWITCH}$INVALID") {
        property("box-shadow", "inset 0 0 0 ${Space.s0_5} ${danger.solid.cssRgb()}")
    }

    rule(".${ControlClass.SWITCH_THUMB}") {
        property("position", "absolute")
        property("top", "${Space.s0_5}")
        property("left", "${Space.s0_5}")
        property("width", "var(${ControlClass.SWITCH_THUMB_VAR})")
        property("height", "var(${ControlClass.SWITCH_THUMB_VAR})")
        property("border-radius", "${Radius.pill}")
        property("background-color", Palette.onAccent.cssRgb())
        property("box-shadow", Shadow.XS)
        property("pointer-events", "none")
        property("transform", "translateX(0) scaleX(1)")
        property("transition", Motion.transitionWith(Motion.DUR_SWITCH_THUMB, Motion.EASE_EMPHASIS, "transform"))
    }
    rule(".${ControlClass.SWITCH}[aria-checked=\"true\"] .${ControlClass.SWITCH_THUMB}") {
        property("transform", "translateX(var(${ControlClass.SWITCH_TRAVEL_VAR})) scaleX(1)")
    }
    rule(".${ControlClass.SWITCH}:active$ENABLED:not([aria-busy=\"true\"]) .${ControlClass.SWITCH_THUMB}") {
        property("transform", "translateX(0) scaleX(var(${ControlClass.SWITCH_SQUEEZE_VAR}))")
    }
    rule(
        ".${ControlClass.SWITCH}[aria-checked=\"true\"]:active$ENABLED:not([aria-busy=\"true\"]) " +
            ".${ControlClass.SWITCH_THUMB}",
    ) {
        property("transform", "translateX(var(${ControlClass.SWITCH_TRAVEL_VAR})) scaleX(var(${ControlClass.SWITCH_SQUEEZE_VAR}))")
    }
}

private fun InitSilkContext.registerPills() {
    val danger = Palette.status(StatusHue.Danger)
    rule(".${ControlClass.DAY_PILL}") {
        property("display", "inline-flex")
        property("align-items", "center")
        property("justify-content", "center")
        property("box-sizing", "border-box")
        property("flex-shrink", "0")
        property("margin", "0")
        property("padding", "0")
        property("font-family", Typography.FONT_SANS)
        property("font-weight", "${FontWeight.medium}")
        property("line-height", "1")
        property("cursor", "pointer")
        property("user-select", "none")
        property("-webkit-appearance", "none")
        property("appearance", "none")
        property("background-color", Palette.surface.cssRgb())
        property("color", Palette.textSecondary.cssRgb())
        property("border", "1px solid ${Palette.border.cssRgb()}")
        property("border-radius", "${Radius.control}")
        property("outline", "none")
        property("transition", feedbackTransition())
        property(Motion.PRESS_SCALE_VAR, Motion.BUTTON_PRESS_SCALE)
    }
    rule(".${ControlClass.DAY_PILL}:hover$ENABLED:not([aria-pressed=\"true\"])") {
        property("border-color", Palette.textTertiary.cssRgb())
        property("background-color", Palette.surfaceHover.cssRgb())
    }
    rule(".${ControlClass.DAY_PILL}[aria-pressed=\"true\"]") {
        property("background-color", Palette.primary.cssRgb())
        property("color", Palette.onPrimary.cssRgb())
        property("border-color", Palette.primary.cssRgb())
    }
    rule(".${ControlClass.DAY_PILL}[aria-pressed=\"true\"]:hover$ENABLED") {
        property("background-color", Palette.primaryHover.cssRgb())
    }
    rule(".${ControlClass.DAY_PILL}:focus-visible") { focusRing() }
    rule(".${ControlClass.DAY_PILL}[aria-disabled=\"true\"]") {
        property("opacity", "0.5")
        property("cursor", "not-allowed")
    }
    rule(".${ControlClass.DAY_PILL}$INVALID:not([aria-pressed=\"true\"])") {
        property("border-color", danger.solid.cssRgb())
    }
}

private fun InitSilkContext.registerListbox() {
    rule(".${ControlClass.OPTION}") {
        property("display", "flex")
        property("align-items", "center")
        property("justify-content", "space-between")
        property("gap", "${Space.s2}")
        property("box-sizing", "border-box")
        property("cursor", "pointer")
        property("user-select", "none")
        property("color", Palette.textPrimary.cssRgb())
        property("border-radius", "${Radius.inner}")
    }
    rule(".${ControlClass.SELECT_SEARCH}") {
        property("flex", "1 1 auto")
        property("min-width", "0")
        property("padding", "0")
        property("margin", "0")
        property("background-color", "transparent")
        property("color", Palette.textPrimary.cssRgb())
        property("border", "none")
        property("border-radius", "${Radius.inner}")
        property("outline", "none")
        property("font-family", Typography.FONT_SANS)
    }
    rule(".${ControlClass.SELECT_SEARCH}::placeholder") { property("color", Palette.textTertiary.cssRgb()) }
    rule(".${ControlClass.SELECT_SEARCH}:focus-visible") {
        property("outline", FocusRing.OUTLINE)
        property("outline-offset", "-${FocusRing.OFFSET_PX}px")
    }

    rule(".${ControlClass.OPTION}[data-active=\"true\"]") {
        property("background-color", Palette.surfaceSubtle.cssRgb())
    }
    rule(".${ControlClass.OPTION}[aria-selected=\"true\"]") {
        property("font-weight", "${FontWeight.medium}")
    }
    rule(".${ControlClass.OPTION}[aria-disabled=\"true\"]") {
        property("opacity", "0.5")
        property("cursor", "not-allowed")
    }

    rule(".${ControlClass.COPY_FIELD}") {
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Space.s2}")
        property("box-sizing", "border-box")
        property("min-width", "0")
        property("background-color", Palette.surfaceSubtle.cssRgb())
        property("border", "1px solid ${Palette.border.cssRgb()}")
        property("border-radius", "${Radius.control}")
        property("font-family", Typography.FONT_MONO)
    }
    rule(".${ControlClass.COPY_FIELD}:focus-within") {
        property("border-color", Palette.accent.cssRgb())
    }
    rule(".${ControlClass.COPY_FIELD_VALUE}") {
        property("flex", "1 1 auto")
        property("min-width", "0")
        property("box-sizing", "border-box")
        property("padding", "0")
        property("margin", "0")
        property("background-color", "transparent")
        property("color", Palette.textPrimary.cssRgb())
        property("border", "none")
        property("outline", "none")
        property("font-family", Typography.FONT_MONO)
        property("text-overflow", "ellipsis")
    }
}
